import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlayIcon, PauseIcon, RotateCcwIcon } from 'lucide-react';
import { PortfolioSnapshot } from '../components/citydesk/PortfolioSnapshot';
import { AgingBuckets } from '../components/citydesk/AgingBuckets';
import { DisputesPanel } from '../components/citydesk/DisputesPanel';
import { RiskPanel } from '../components/citydesk/RiskPanel';
import { AlertsFeed } from '../components/citydesk/AlertsFeed';
import { RiskDecisions } from '../components/citydesk/RiskDecisions';
interface DemoState {
  phase: number;
  portfolioData: {
    totalFunded: number;
    outstanding: number;
    weightedTenor: number;
    currentYield: number;
    portfolioHealth: number;
  };
  agingHighlight: boolean;
  disputesPct: number;
  concentrationWarning: boolean;
  concentrationPct: number;
  alerts: Array<{
    id: string;
    time: string;
    level: string;
    message: string;
  }>;
  decisions: Array<{
    id: string;
    time: string;
    actor: string;
    action: string;
    result: string;
  }>;
  systemNote: string;
  isRecomputing: boolean;
}
export function CityDesk() {
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const [demoState, setDemoState] = useState<DemoState>({
    phase: 0,
    portfolioData: {
      totalFunded: 847200000,
      outstanding: 623400000,
      weightedTenor: 42,
      currentYield: 8.7,
      portfolioHealth: 96.2
    },
    agingHighlight: false,
    disputesPct: 0,
    concentrationWarning: false,
    concentrationPct: 18.2,
    alerts: [],
    decisions: [],
    systemNote: '',
    isRecomputing: false
  });
  useEffect(() => {
    document.title = 'Mint | City Desk';
  }, []);
  useEffect(() => {
    if (!isPlaying) return;
    const interval = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev + 100 / 60; // 60 seconds total
        if (newProgress >= 100) {
          setIsPlaying(false);
          return 100;
        }
        return newProgress;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [isPlaying]);
  // Auto-play sequence orchestration - compressed to 60s
  useEffect(() => {
    if (!isPlaying) return;
    const currentSecond = Math.floor(progress / 100 * 60);
    // Phase 1: Portfolio snapshot (0-5s)
    if (currentSecond >= 0 && currentSecond < 5 && demoState.phase !== 1) {
      setDemoState(prev => ({
        ...prev,
        phase: 1,
        systemNote: 'System note: portfolio metrics updated from Mint rail.',
        isRecomputing: false
      }));
    }
    // Phase 2: Aging buckets highlight (5-10s)
    if (currentSecond >= 5 && currentSecond < 10 && demoState.phase !== 2) {
      setDemoState(prev => ({
        ...prev,
        phase: 2,
        agingHighlight: true,
        systemNote: 'System note: aging profile updated from latest batch.',
        isRecomputing: false
      }));
    }
    // Phase 3: Disputes update (10-15s)
    if (currentSecond >= 10 && currentSecond < 15 && demoState.phase !== 3) {
      setDemoState(prev => ({
        ...prev,
        phase: 3,
        disputesPct: 2.1,
        systemNote: 'System note: disputes mix recalculated from live portfolio.',
        isRecomputing: false
      }));
    }
    // Phase 4: Concentration warning (15-22s)
    if (currentSecond >= 15 && currentSecond < 22 && demoState.phase !== 4) {
      setDemoState(prev => ({
        ...prev,
        phase: 4,
        concentrationWarning: true,
        systemNote: 'System note: concentration checks re-run on new tickets.',
        isRecomputing: false
      }));
    }
    // Phase 5: Alerts feed (22-35s)
    if (currentSecond >= 22 && currentSecond < 35 && demoState.phase !== 5) {
      const newAlerts = [{
        id: '1',
        time: '09:47',
        level: 'info',
        message: 'Obligor XYZ rating upgraded A- → A'
      }, {
        id: '2',
        time: '09:46',
        level: 'warn',
        message: 'Model anomaly: sector drift +2.3%'
      }, {
        id: '3',
        time: '09:45',
        level: 'success',
        message: 'Payment trend: 3-day acceleration'
      }, {
        id: '4',
        time: '09:44',
        level: 'warn',
        message: 'Concentration threshold: 85% utilized'
      }, {
        id: '5',
        time: '09:43',
        level: 'success',
        message: 'Dispute resolved: $127K recovered'
      }];
      setDemoState(prev => ({
        ...prev,
        phase: 5,
        alerts: newAlerts,
        systemNote: 'System note: alert feed updated with model outputs.',
        isRecomputing: false
      }));
    }
    // Phase 6: Risk decisions (35-45s)
    if (currentSecond >= 35 && currentSecond < 45 && demoState.phase !== 6) {
      const newDecisions = [{
        id: '1',
        time: '09:40',
        actor: 'risk@mint.us',
        action: 'Limit override: Obligor ABC +$5M',
        result: 'Applied to pipeline'
      }, {
        id: '2',
        time: '09:35',
        actor: 'system',
        action: 'Pricing tightened: Sector retail +25bps',
        result: 'Live and monitored'
      }, {
        id: '3',
        time: '09:30',
        actor: 'committee',
        action: 'Diversification guidance: Reduce tech exposure',
        result: 'Pending review'
      }, {
        id: '4',
        time: '09:25',
        actor: 'committee',
        action: 'Committee signoff: Q4 concentration limits',
        result: 'Approved'
      }];
      setDemoState(prev => ({
        ...prev,
        phase: 6,
        decisions: newDecisions,
        systemNote: 'System note: risk decisions logged for audit trail.',
        isRecomputing: false
      }));
    }
    // Phase 7: New invoice + RECOMPUTE trigger (45-50s)
    if (currentSecond >= 45 && currentSecond < 50 && demoState.phase !== 7) {
      setDemoState(prev => ({
        ...prev,
        phase: 7,
        alerts: [{
          id: 'new',
          time: '09:48',
          level: 'info',
          message: 'New invoice: $2.4M, 45-day tenor'
        }, ...prev.alerts],
        systemNote: 'System note: new ticket entered. Triggering full recompute...',
        isRecomputing: true
      }));
    }
    // Phase 8: RECOMPUTE COMPLETE - all metrics update (50-55s)
    if (currentSecond >= 50 && currentSecond < 55 && demoState.phase !== 8) {
      setDemoState(prev => ({
        ...prev,
        phase: 8,
        portfolioData: {
          ...prev.portfolioData,
          outstanding: 625800000,
          weightedTenor: 43,
          currentYield: 8.8
        },
        concentrationPct: 17.9,
        concentrationWarning: false,
        disputesPct: 1.8,
        systemNote: 'System note: recompute complete. Exposure shifted, yield updated, tenor adjusted.',
        isRecomputing: false
      }));
    }
    // Phase 9: Final state (55-60s)
    if (currentSecond >= 55 && demoState.phase !== 9) {
      setDemoState(prev => ({
        ...prev,
        phase: 9,
        systemNote: 'System note: live credit rail running end-to-end.',
        isRecomputing: false
      }));
    }
  }, [progress, isPlaying, demoState.phase]);
  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };
  const handleReplay = () => {
    setProgress(0);
    setIsPlaying(true);
    setDemoState({
      phase: 0,
      portfolioData: {
        totalFunded: 847200000,
        outstanding: 623400000,
        weightedTenor: 42,
        currentYield: 8.7,
        portfolioHealth: 96.2
      },
      agingHighlight: false,
      disputesPct: 0,
      concentrationWarning: false,
      concentrationPct: 18.2,
      alerts: [],
      decisions: [],
      systemNote: '',
      isRecomputing: false
    });
  };
  return <div className="min-h-screen bg-slate-950" data-id="element-1344">
      {/* Header - institutional terminal style */}
      <div className="border-b border-slate-800 bg-slate-900/50 backdrop-blur-sm" data-id="element-1345">
        <div className="px-6 py-4" data-id="element-1346">
          {/* Top chrome bar - subtle branding + controls */}
          <div className="flex items-center justify-between mb-4" data-id="element-1347">
            <div className="flex items-center gap-3" data-id="element-1348">
              <span className="text-xs font-mono text-slate-500" data-id="element-1349">Mint</span>
              <div className="flex items-center gap-1.5" data-id="element-1350">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" data-id="element-1351" />
                <span className="text-[10px] font-mono text-slate-500 uppercase" data-id="element-1352">
                  LIVE
                </span>
              </div>
            </div>

            {/* Icon-only controls */}
            <div className="flex items-center gap-2" data-id="element-1353">
              <button onClick={handlePlayPause} title={isPlaying ? 'Pause sequence' : 'Play sequence'} className="p-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded transition-colors" data-id="element-1354">
                {isPlaying ? <PauseIcon size={14} data-id="element-1355" /> : <PlayIcon size={14} data-id="element-1356" />}
              </button>
              <button onClick={handleReplay} title="Replay sequence" className="p-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded transition-colors" data-id="element-1357">
                <RotateCcwIcon size={14} data-id="element-1358" />
              </button>
            </div>
          </div>

          {/* Main title - larger, stronger hierarchy */}
          <div className="text-center mb-4" data-id="element-1359">
            <h1 className="text-[38px] font-semibold text-slate-100 tracking-tight mb-2" style={{
            fontFamily: 'Inter, system-ui, sans-serif',
            fontWeight: 600
          }} data-id="element-1360">
              CITY DESK
            </h1>
            <p className="text-[17px] text-slate-400" data-id="element-1361">
              Real-Time Credit Rail Monitor
            </p>
          </div>

          {/* Progress bar - thicker for "system heartbeat" */}
          <div className="mb-1.5" data-id="element-1362">
            <div className="h-[0.6px] bg-slate-800 rounded-full overflow-hidden" data-id="element-1363">
              <motion.div className="h-full bg-emerald-500" initial={{
              width: 0
            }} animate={{
              width: `${progress}%`
            }} transition={{
              duration: 0.2
            }} data-id="element-1364" />
            </div>
          </div>

          {/* System note strip - tighter padding, slightly bolder */}
          <AnimatePresence mode="wait" data-id="element-1365">
            {demoState.systemNote && <motion.div key={demoState.systemNote} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.2
          }} className={`px-3 py-1 rounded border ${demoState.isRecomputing ? 'bg-emerald-900/30 border-emerald-700/50' : 'bg-slate-800/30 border-slate-800/50'}`} data-id="element-1366">
                <p className={`text-[11px] font-mono ${demoState.isRecomputing ? 'text-emerald-400 font-medium' : 'text-slate-400 font-medium'}`} data-id="element-1367">
                  {demoState.systemNote}
                </p>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>

      {/* Recompute overlay flash */}
      <AnimatePresence data-id="element-1368">
        {demoState.isRecomputing && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: [0, 0.15, 0]
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.6,
        times: [0, 0.5, 1]
      }} className="fixed inset-0 bg-emerald-500 pointer-events-none z-50" data-id="element-1369" />}
      </AnimatePresence>

      {/* Main grid - tighter spacing */}
      <div className="p-4" data-id="element-1370">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3" data-id="element-1371">
          {/* Top row */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0,
          scale: demoState.isRecomputing ? [1, 0.98, 1] : 1
        }} transition={{
          delay: 0.05,
          duration: 0.2,
          scale: {
            duration: 0.4
          }
        }} data-id="element-1372">
            <PortfolioSnapshot data={demoState.portfolioData} phase={demoState.phase} isRecomputing={demoState.isRecomputing} data-id="element-1373" />
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0,
          scale: demoState.isRecomputing ? [1, 0.98, 1] : 1
        }} transition={{
          delay: 0.1,
          duration: 0.2,
          scale: {
            duration: 0.4,
            delay: 0.1
          }
        }} data-id="element-1374">
            <AgingBuckets highlight={demoState.agingHighlight} phase={demoState.phase} isRecomputing={demoState.isRecomputing} data-id="element-1375" />
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0,
          scale: demoState.isRecomputing ? [1, 0.98, 1] : 1
        }} transition={{
          delay: 0.15,
          duration: 0.2,
          scale: {
            duration: 0.4,
            delay: 0.2
          }
        }} data-id="element-1376">
            <DisputesPanel disputesPct={demoState.disputesPct} phase={demoState.phase} isRecomputing={demoState.isRecomputing} data-id="element-1377" />
          </motion.div>

          {/* Bottom row */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0,
          scale: demoState.isRecomputing ? [1, 0.98, 1] : 1
        }} transition={{
          delay: 0.2,
          duration: 0.2,
          scale: {
            duration: 0.4,
            delay: 0.3
          }
        }} data-id="element-1378">
            <RiskPanel showWarning={demoState.concentrationWarning} concentrationPct={demoState.concentrationPct} phase={demoState.phase} isRecomputing={demoState.isRecomputing} data-id="element-1379" />
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0,
          scale: demoState.isRecomputing ? [1, 0.98, 1] : 1
        }} transition={{
          delay: 0.25,
          duration: 0.2,
          scale: {
            duration: 0.4,
            delay: 0.4
          }
        }} data-id="element-1380">
            <AlertsFeed alerts={demoState.alerts} phase={demoState.phase} isRecomputing={demoState.isRecomputing} data-id="element-1381" />
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0,
          scale: demoState.isRecomputing ? [1, 0.98, 1] : 1
        }} transition={{
          delay: 0.3,
          duration: 0.2,
          scale: {
            duration: 0.4,
            delay: 0.5
          }
        }} data-id="element-1382">
            <RiskDecisions decisions={demoState.decisions} phase={demoState.phase} isRecomputing={demoState.isRecomputing} data-id="element-1383" />
          </motion.div>
        </div>
      </div>
    </div>;
}